/**
 * useKeyboardNav hook
 * Handle keyboard navigation for dropdown menus and modals
 * Supports Escape key for closing and can be extended for arrow key navigation
 */

import { useEffect } from 'react';

/**
 * Options for keyboard navigation hook
 */
interface UseKeyboardNavOptions {
  /** Callback function when Escape key is pressed */
  onEscape?: () => void;
  /** Whether keyboard navigation is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Hook for keyboard navigation in dropdown menus
 * Currently supports Escape key for closing dropdowns
 * Can be extended to support arrow key navigation in the future
 *
 * @param options - Configuration options for keyboard navigation
 *
 * @example
 * ```typescript
 * useKeyboardNav({
 *   onEscape: () => setDropdownOpen(false),
 *   enabled: dropdownOpen
 * });
 * ```
 */
export function useKeyboardNav(options: UseKeyboardNavOptions): void {
  const { onEscape, enabled = true } = options;

  useEffect(() => {
    // Don't bind listeners if disabled
    if (!enabled) return;

    /**
     * Handle keyboard events
     * @param event - Keyboard event
     */
    function handleKeyDown(event: KeyboardEvent): void {
      switch (event.key) {
        case 'Escape':
          // Close dropdown/modal on Escape
          onEscape?.();
          break;

        // Future enhancement: Arrow key navigation
        // case 'ArrowDown':
        //   // Navigate to next menu item
        //   break;
        // case 'ArrowUp':
        //   // Navigate to previous menu item
        //   break;
        // case 'Enter':
        //   // Activate current menu item
        //   break;

        default:
          break;
      }
    }

    // Bind the event listener to document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup: Remove event listener on unmount or when disabled
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onEscape, enabled]); // Re-run effect if callbacks or enabled state changes
}
